import type { SimulatorType } from '@/ui'
import type { OrgAssets } from '@/ui'
import { autoUpdate, FloatingPortal, hide, offset, shift, size, useFloating } from '@floating-ui/react'
import type { FontData } from '@jobilla/nexus-sdk'
import { createStore, useAtomValue } from 'jotai'
import { XIcon } from 'lucide-react'
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import {
	canvasCustomFontsAtom,
	canvasCustomFontsLoadingAtom,
	canvasIsGeneratingAtom,
	canvasOnAssetsUploadAtom,
	canvasOrgAssetsAtom,
	canvasRootElAtom,
	canvasSimulatorTypeAtom,
	canvasSizeAtom,
	documentAtom,
	expandedLayerIdsAtom,
	externalCanvasDataAtom,
	fontLoadingCountAtom,
	highlightedLayerIdAtom,
	layerAtomFamily,
	onCommitAtom,
	onExternalCanvasDataChangeAtom,
	type SafeZone,
	selectedLayerIdAtom,
} from './atoms'
import { CanvasStoreContext, SafeZoneContext, useCanvasStore } from './canvas-store'
import { CanvasData } from './data'
import { LayerGizmo } from './gizmo'
import { FOCUSABLE_LAYER_TYPES } from './gizmo/constants'
import { LayerRenderer } from './layers'
import { CanvasToolbar } from './toolbar/toolbar'
import { collectContainerIds } from './use-layer-tree-actions'

export type { SafeZone } from './atoms'

export interface ExternalCanvasData {
	title: string
	subtitle: string
	location: string
	cta: string

	logoUrl: string
	backgroundUrl: string

	foregroundColor: string
	backgroundColor: string
	accentForegroundColor: string
	accentBackgroundColor: string
}

export type CanvasContainerRef = {
	setDocument: (document: CanvasData) => void
}

export type CanvasContainerProps = {
	ref?: React.Ref<CanvasContainerRef>
	width: number
	height: number

	// this component is uncontrolled; it will only parse the document once.
	// use the imperative ref to update the document after mount.
	document: CanvasData
	onDocumentChange: (document: CanvasData) => void

	externalCanvasData: ExternalCanvasData
	onExternalCanvasDataChange: (externalCanvasData: Partial<ExternalCanvasData>) => void

	safeZone?: SafeZone

	orgAssets?: OrgAssets
	onAssetsUpload?: (files: FileList, collection?: string) => void

	customFonts?: FontData[]
	customFontsLoading?: boolean

	isGenerating?: boolean

	simulatorType?: SimulatorType | null

	showToolbar?: boolean
}

const defaultSafeZone: SafeZone = { top: 0, right: 0, bottom: 0, left: 0 }

export function CanvasContainer(
	{
		ref,
		width,
		height,
		document,
		onDocumentChange,
		externalCanvasData,
		onExternalCanvasDataChange,
		safeZone = defaultSafeZone,
		orgAssets,
		onAssetsUpload,
		customFonts,
		customFontsLoading,
		isGenerating,
		simulatorType,
		showToolbar = true,
	}: CanvasContainerProps,
) {
	// Configure jotai store -- load document.
	// Right now we're setting the default jotai store via the standard jotai context.
	// If we ever need to overlap with other stores, we can make a new context for the store here.
	const store = useMemo(() => {
		const s = createStore()
		s.set(documentAtom, document)
		s.set(externalCanvasDataAtom, externalCanvasData)
		s.set(canvasSizeAtom, { width, height })
		s.set(expandedLayerIdsAtom, collectContainerIds(document))
		return s
	}, [])

	useImperativeHandle(ref, () => ({
		setDocument(doc: CanvasData) {
			store.set(documentAtom, doc)
			store.set(selectedLayerIdAtom, null)
			store.set(expandedLayerIdsAtom, collectContainerIds(doc))
		},
	}), [store])

	// Keep the callback accessible from the store
	useEffect(() => {
		store.set(onCommitAtom, () => onDocumentChange)
	}, [onDocumentChange])

	// Sync external data when the prop changes
	useEffect(() => {
		store.set(externalCanvasDataAtom, externalCanvasData)
	}, [externalCanvasData])

	// Keep the external data change callback in sync
	useEffect(() => {
		store.set(onExternalCanvasDataChangeAtom, () => onExternalCanvasDataChange)
	}, [onExternalCanvasDataChange])

	// Sync canvas size when props change
	useEffect(() => {
		store.set(canvasSizeAtom, { width, height })
	}, [width, height])

	// NOTE: the safe zone is deliberately NOT synced into a jotai atom — it is
	// provided via SafeZoneContext below so content-layer margins commit in the
	// same flush as the canvas width/height. See SafeZoneContext in
	// canvas-store.tsx for the Chrome ≥150 layout bug this avoids.

	// Sync org assets when the prop changes
	useEffect(() => {
		store.set(canvasOrgAssetsAtom, orgAssets ?? { media: [] })
	}, [orgAssets])

	// Sync custom fonts when the prop changes
	useEffect(() => {
		store.set(canvasCustomFontsAtom, customFonts ?? [])
	}, [customFonts])
	useEffect(() => {
		store.set(canvasCustomFontsLoadingAtom, customFontsLoading ?? false)
	}, [customFontsLoading])

	// Sync generation state
	useEffect(() => {
		store.set(canvasIsGeneratingAtom, isGenerating ?? false)
	}, [isGenerating])

	useEffect(() => {
		store.set(canvasSimulatorTypeAtom, simulatorType ?? null)
	}, [simulatorType])

	// Keep the upload callback in sync
	useEffect(() => {
		if (onAssetsUpload) {
			store.set(canvasOnAssetsUploadAtom, () => onAssetsUpload)
		}
		return () => {
			store.set(canvasOnAssetsUploadAtom, null)
		}
	}, [onAssetsUpload])

	// toolbar floater
	const [toolbarOpen, setToolbarOpen] = useState(false)
	const toolbarOpenRef = useRef(toolbarOpen)
	toolbarOpenRef.current = toolbarOpen

	const { refs, floatingStyles, middlewareData, update } = useFloating({
		placement: 'left',
		middleware: [
			offset(16),
			shift(),
			size({
				apply({ availableWidth, elements }) {
					if (!toolbarOpenRef.current) {
						elements.floating.style.width = ''
						return
					}
					const w = `${Math.min(350, availableWidth)}px`
					elements.floating.style.width = w
					elements.floating.style.setProperty('--toolbar-width', w)
				},
			}),
			hide(),
		],
		whileElementsMounted: autoUpdate,
	})

	const referenceHidden = middlewareData.hide?.referenceHidden

	const toggleToolbar = useCallback((open: boolean) => {
		flushSync(() => setToolbarOpen(open))
		update()
	}, [update])

	// Deselect layer when clicking outside the canvas and toolbar.
	// Only consider clicks inside #root — portaled elements (dropdowns, popovers)
	// live outside #root and should never trigger deselection.
	useEffect(() => {
		function handlePointerDown(e: PointerEvent) {
			const target = e.target as HTMLElement
			if (!target.closest?.('#root')) { return }
			const canvasEl = refs.reference.current as HTMLElement | null
			const toolbarEl = refs.floating.current
			if (canvasEl?.contains(target)) { return }
			if (toolbarEl?.contains(target)) { return }
			store.set(selectedLayerIdAtom, null)
		}
		window.document.addEventListener('pointerdown', handlePointerDown)
		return () => window.document.removeEventListener('pointerdown', handlePointerDown)
	}, [store, refs])

	const canvasRootRef = useCallback((el: HTMLElement | null) => {
		refs.setReference(el)
		store.set(canvasRootElAtom, el)
	}, [refs, store])

	return (
		<>
			<CanvasStoreContext.Provider value={store}>
				<SafeZoneContext.Provider value={safeZone}>
					<div
						ref={canvasRootRef}
						style={{
							width: `${width}px`,
							height: `${height}px`,
							'--canvas-width': `${width}px`,
							'--canvas-height': `${height}px`,
						} as React.CSSProperties}
						className="bg-gradient-to-b from-grey-800 to-grey-900 relative text-white ring ring-grey-200 overflow-hidden"
						onClick={(e) => {
							const target = (e.target as HTMLElement).closest<HTMLElement>('[data-layer-id]')
							if (target) {
								const layerId = target.dataset.layerId!
								const layer = store.get(layerAtomFamily(layerId))
								if (layer && FOCUSABLE_LAYER_TYPES.has(layer.type) && !layer.locked) {
									store.set(selectedLayerIdAtom, layerId)
									return
								}
							}
						}}
						onPointerOver={(e) => {
							const target = (e.target as HTMLElement).closest<HTMLElement>('[data-layer-id]')
							if (target) {
								const layerId = target.dataset.layerId!
								const layer = store.get(layerAtomFamily(layerId))
								if (layer && !layer.locked) {
									store.set(highlightedLayerIdAtom, layerId)
								}
							}
						}}
						onPointerOut={(e) => {
							const target = (e.target as HTMLElement).closest<HTMLElement>('[data-layer-id]')
							if (target) {
								const related = (e.relatedTarget as HTMLElement | null)?.closest<HTMLElement>(
									'[data-layer-id]',
								)
								if (related?.dataset.layerId !== target.dataset.layerId) {
									store.set(highlightedLayerIdAtom, null)
								}
							}
						}}
					>
						<LayerRenderer />
						<FontLoadingIndicator />
					</div>
					<LayerGizmo />
					{showToolbar && (
						<FloatingPortal>
							<div
								ref={refs.setFloating}
								className="z-50"
								style={{
									...floatingStyles,
									...(referenceHidden && { visibility: 'hidden' }),
								}}
							>
								{toolbarOpen
									? (
										<div className="relative">
											<button
												type="button"
												onClick={() => toggleToolbar(false)}
												className="absolute top-1.5 right-3.5 z-10 flex items-center justify-center size-6 rounded bg-grey-100 hover:bg-grey-200 text-grey-500 transition-colors cursor-pointer"
												title="Collapse toolbar"
												aria-label="Collapse toolbar"
											>
												<XIcon className="size-3.5" aria-hidden="true" />
											</button>
											<CanvasToolbar />
										</div>
									)
									: (
										<button
											type="button"
											onClick={() => toggleToolbar(true)}
											className="m-2 flex items-center justify-center size-9 rounded border border-grey-100 bg-white text-grey-600 shadow hover:bg-grey-50 transition-colors cursor-pointer"
											title="Open layers panel"
											aria-label="Open layers panel"
										>
											<DesignToolIcon aria-hidden="true" />
										</button>
									)}
							</div>
						</FloatingPortal>
					)}
				</SafeZoneContext.Provider>
			</CanvasStoreContext.Provider>
		</>
	)
}

function FontLoadingIndicator() {
	const store = useCanvasStore()
	const count = useAtomValue(fontLoadingCountAtom, { store })
	const [visible, setVisible] = useState(false)

	useEffect(() => {
		if (count === 0) {
			setVisible(false)
			return
		}
		const timer = setTimeout(() => setVisible(true), 1000)
		return () => clearTimeout(timer)
	}, [count])

	if (!visible) { return null }

	return (
		<div
			data-render-exclude="true"
			className="animate-pulse absolute inset-x-0 top-0 z-10 flex items-center justify-center bg-black/40 py-1 text-[10px] text-white/80 font-medium tracking-wide"
		>
			Loading fonts...
		</div>
	)
}

function DesignToolIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 14 14" {...props}>
			<path
				d="M6.729758 0.525574C6.548150000000001 0.56168 6.431082 0.60865 6.048 0.7991199999999999C4.895169999999999 1.372322 1.014398 3.335808 0.956662 3.375106C0.779702 3.495548 0.620676 3.7119040000000005 0.55104 3.927C0.50225 4.077668 0.490126 4.324852 0.523586 4.4866079999999995C0.577752 4.748534 0.751688 5.011496 0.959364 5.145462C1.082788 5.22508 6.4086680000000005 7.881075999999999 6.519478 7.918274C6.822746 8.020082 7.177184 8.019662 7.484567999999999 7.917167999999999C7.612261999999999 7.874580000000001 12.900342 5.217954000000001 13.026832 5.132834C13.329582 4.92912 13.496 4.61314 13.496 4.242C13.496 3.8798060000000003 13.32107 3.542056 13.033734 3.3495C12.920964000000001 3.273928 7.570976 0.608244 7.467964000000001 0.57631C7.2338700000000005 0.50372 6.94092 0.483588 6.729758 0.525574M6.91712 1.513428C6.836466000000001 1.524152 6.912696 1.4863380000000002 3.143196 3.3862080000000003C2.214492 3.854284 1.525398 4.209604 1.5145060000000001 4.226012C1.50381 4.242112 1.499666 4.267186 1.504944 4.283832C1.512056 4.306218 2.148426 4.630066 4.176634 5.643470000000001C6.350624 6.729702 6.854862 6.97655 6.9254359999999995 6.989136C6.996192000000001 7.00175 7.028406 7.000476 7.102914 6.982080000000001C7.222824000000001 6.952484 12.484458 4.3031939999999995 12.496091999999999 4.2665560000000005C12.500376000000001 4.253046 12.499801999999999 4.229148 12.494818 4.2134540000000005C12.48807 4.192174 11.81229 3.848222 9.84438 2.864456C8.391628 2.1382060000000003 7.180949999999999 1.537368 7.154 1.529248C7.081382 1.507352 7.002352 1.502074 6.91712 1.513428M0.843962 6.877878C0.680764 6.93623 0.591472 7.024150000000001 0.527926 7.189C0.49086799999999997 7.285152000000001 0.49988400000000005 7.469574000000001 0.54607 7.560252C0.592606 7.6516020000000005 0.641228 7.710794 0.705922 7.75488C0.809214 7.825258 6.402522 10.637578 6.511176 10.673768C6.642202 10.717406 6.8488 10.751902000000001 6.979 10.75186C7.116382 10.751818 7.322938000000001 10.718176 7.455 10.674328000000001C7.559748 10.639538 13.082678 7.8810340000000005 13.260786 7.7745500000000005C13.346480000000001 7.72331 13.403432 7.66017 13.454084 7.560252C13.500102 7.469448000000001 13.509048 7.284941999999999 13.472074000000001 7.189C13.408122 7.023072000000001 13.310108 6.927704 13.149206000000001 6.87484C13.045172 6.840666000000001 12.966632 6.838412000000001 12.870802 6.866874C12.832162 6.878354 11.544232 7.518503999999999 10.008768 8.289428C8.47329 9.060352 7.182938 9.703008 7.1413020000000005 9.717554C7.051912000000001 9.748788 6.91411 9.752036 6.837264 9.724708C6.807416000000001 9.714096000000001 5.526528 9.071384 3.9908260000000007 8.296456000000001C2.455138 7.521527999999999 1.167208 6.878158000000001 1.128792 6.866748C1.031968 6.837992 0.946078 6.8413520000000005 0.843962 6.877878M0.9296559999999999 9.606632C0.7674240000000001 9.630544 0.626024 9.733066 0.552454 9.880108C0.497392 9.990204 0.48827800000000005 10.147956 0.5301100000000001 10.266998C0.563514 10.36203 0.635306 10.455298 0.7183820000000001 10.511578C0.8267140000000001 10.584966 6.389138 13.381438000000001 6.501768 13.41914C6.667584 13.474636 6.802138 13.496 6.986 13.496C7.1697500000000005 13.496 7.30471 13.474594 7.469266 13.41935C7.529662 13.399078 8.861566000000002 12.738978000000001 10.429048 11.952444C13.096944 10.61375 13.283704 10.51771 13.352682000000001 10.448928C13.460776000000001 10.341142 13.487558000000002 10.272458 13.488356 10.101C13.488944 9.974314 13.485248 9.953678 13.449562 9.884C13.406834 9.800602 13.331738000000001 9.71537 13.265 9.674546C13.151096 9.604854000000001 12.960682 9.581362 12.845 9.622732C12.8142 9.633750000000001 11.526578 10.27642 9.983624 11.050914C8.440669999999999 11.825394000000001 7.155708000000001 12.466510000000001 7.12817 12.475596000000001C7.062146 12.49738 6.9076699999999995 12.496666000000001 6.839742 12.474252C6.809964 12.464424000000001 5.528166000000001 11.822034 3.991302 11.046728C2.454438 10.271436 1.1844 9.63221 1.169 9.626232C1.115772 9.605582000000002 1.000734 9.596160000000001 0.9296559999999999 9.606632"
				stroke="none"
				fill="currentColor"
				fillRule="evenodd"
				strokeWidth={0.014}
			/>
		</svg>
	)
}
